import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

function lines(text: string): string[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

export function CreateClaimPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromSuggestion = params.get("suggestion");
  const [label, setLabel] = useState(params.get("label") ?? "");
  const [topic, setTopic] = useState("");
  const [aliases, setAliases] = useState("");
  const [phrases, setPhrases] = useState("");
  const [triggers, setTriggers] = useState("");
  const [readingQuery, setReadingQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      const claim = await api.createClaim({
        label,
        topic,
        aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
        phrases: lines(phrases),
        trigger_terms: lines(triggers),
        reading_query: readingQuery.trim(),
      });
      if (fromSuggestion) {
        await api
          .reviewSuggestion(fromSuggestion, {
            status: "accepted",
            review_note: "Accepted — a curator has started researching this idea.",
            linked_claim_id: claim.id,
          })
          .catch(() => undefined);
      }
      navigate(`/claims/${claim.id}/versions/new`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create this claim.");
    }
  }

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>New claim</h1>
      {fromSuggestion && <p style={s.mutedText}>Started from a reader's suggestion — it will be marked accepted and linked to this claim.</p>}
      <form onSubmit={(e) => void handleSubmit(e)} style={{ maxWidth: 640 }}>
        <label style={s.label}>
          Label (how the claim is usually stated)
          <input style={s.input} required value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label style={s.label}>
          Topic (short id, e.g. <code>study_strategies</code>)
          <input style={s.input} required value={topic} onChange={(e) => setTopic(e.target.value)} />
        </label>
        <label style={s.label}>
          Aliases (comma-separated — other names for the idea)
          <input style={s.input} value={aliases} onChange={(e) => setAliases(e.target.value)} />
        </label>
        <label style={s.label}>
          Example phrasings (one per line — typical sentences that state the idea; used by the server to recognise it)
          <textarea style={s.textarea} value={phrases} onChange={(e) => setPhrases(e.target.value)} />
        </label>
        <label style={s.label}>
          On-device trigger phrases (one per line — short phrases like “learning style”; the extension only looks closer at
          passages containing one of these, so keep them specific)
          <textarea style={s.textarea} value={triggers} onChange={(e) => setTriggers(e.target.value)} />
        </label>
        <label style={s.label}>
          Research search phrases (separate with “;” — used to find highly cited, open-access articles about this idea in
          OpenAlex and ERIC, e.g. “learning styles; meshing hypothesis”)
          <input style={s.input} value={readingQuery} onChange={(e) => setReadingQuery(e.target.value)} maxLength={500} />
        </label>
        {error && <p style={s.errorText}>{error}</p>}
        <button type="submit" style={s.buttonPrimary}>
          Create claim
        </button>
      </form>
    </div>
  );
}
